import { globals } from 'config/globals';
import { InertiaDrag, Point } from 'interaction/InertiaDrag';
import { constrain } from 'utils/math';
import { MouseController } from 'app/chrome/mouse/MouseController';
import { App } from 'core/App';
import DelayHelper from 'utils/timers';
import { device } from 'utils/device';

interface Breakpoint {
	ratio: number;
	lower: number;
	upper: number;
}

export enum axisType {
	X,
	Y
}
export enum DragMotionType {
	RATIO,
	ONE_TO_ONE
}

export class DragBreakpoints {
	public easePower: number = 10.0;
	public releaseEasePower: number = 15.0;

	public onSnap: Function;
	public onTick: Function;
	public onStartDrag: Function;
	public onStopDrag: Function;
	public onPreDrag: Function;
	public axis: axisType = axisType.X;
	public forceSnap: boolean = true;
	public mouseController: MouseController;
	public simulateSteps: number = 60;
	public snapWidth: number = null;
	public snapOnlyOnTouch: boolean = false;

	private _dragMotionType: DragMotionType = DragMotionType.RATIO;
	private _active: boolean = false;
	private _cancelled: boolean = false;
	private _progress: number = 0;
	private _targetProgress: number = 0;
	private _breakpoints: Array<Breakpoint> = [];
	private _breakpointShare: number;
	private _breakpointIndex: number = 0;
	private _inertiaDrag: InertiaDrag;
	private _tweening: boolean;
	private _snapping: boolean;
	private _easePower: number = 10;
	private _targetEasePower: number = 10;
	private _targetDelta: number = 0.9;

	private _virtualWidth: number = 2000; //Fake width to allow
	private _innerWidth: number;
	private _dragToMoveRatio: number = 0.25; //User needs to move 25% of the screen to do a full move across
	private _overflowZoneTolerence: number = 0.05; //If drag is started
	private _overflowAmount: number = 0.04;
	private _overflowZoneActive: boolean = false;

	private _defaultConstraints: any = { min: 0, max: 1 };
	private _constraints: any = { min: 0, max: 1 };


	private _simluateRestrictWidth: number = 1.0; //Amount of width that can be simlated on swipe

	private _startIndex: number = 0;

	private _randomId = Math.floor(Math.random() * 10000);

	constructor(
		count: number,
		public loop: boolean = false,
		startIndex: number = 0,
		public snap: boolean = true,
		$target: Element = null
	) {
		this._breakpointShare = 1.0 / count;
		this._breakpointIndex = startIndex;
		this._bindToAppTick();
		this._createBreakpoints(count);
		this._addInertiaDrag($target);
	}

	public set eventTag(v: string) {
		this._inertiaDrag.eventTag = v;
	}

	public get targetProgress() {
		return this._targetProgress;
	}

	public get breakpointIndex() {
		return this._breakpointIndex;
	}

	public get active(): boolean {
		return this._active;
	}

	public get thresholdReached(): boolean {
		return this._progress <= 0.03 || this._progress >= 0.97;
	}

	public get isDragging(): boolean {
		return this._inertiaDrag.isDragging;
	}

	public setActive(active: boolean): void {
		this._active = active;
		this._inertiaDrag.setActive(this._active);
	}

	public setAllowDrag(allowDrag: boolean): void {
		this._inertiaDrag.setAllowDrag(allowDrag);
	}

	/** Cancel a drag action */
	public cancel(): void {
		this._inertiaDrag.forceStop(true);
	}

	public destroy(): void {
		this._active = false;
		this._inertiaDrag.destroy();
		App.instance.removePreTickById('dragBreakpoints' + this._randomId);
	}

	public removeVelocity(): void {
		this._inertiaDrag.removeVelocity();
	}

	/** Set the imaginary width of the container being moved. I.E x3 screen width */
	public setVirtualWidth(
		width: number,
		innerWidth: number = -1,
		itemWidth = null,
		itemSpacing = null,
		//Optional: only used when snapWidth is set (callers routinely pass width only):
		totalItems?,
		snapItems?
	): void {
		this._virtualWidth = width;
		this._innerWidth = innerWidth == -1 ? device.innerWidth : innerWidth;

		if (itemWidth) {
			this.snapWidth = itemWidth;
		}

		if (this.snapWidth !== null) {
			this._createBreakpoints(-1, itemSpacing, totalItems, snapItems);
		}
	}

	/** Set the ratio on mouch the user needs to move to drag the screen. I smaller number means more responsive */
	public setDragToMoveRatio(ratio: number): void {
		this._dragToMoveRatio = ratio;
	}

	/** Set default contrains of drag action. Useful to stop early like on Work section */
	public setConstraints(min: number = 0, max: number = 1): void {
		this._defaultConstraints = {
			min: min,
			max: max
		};
	}

	//** Override the progress */
	public setProgress(ratio: number): void {
		this._progress = this._targetProgress = ratio;
	}

	public hardSetEase(ease) {
		this._targetEasePower = ease;
		this._easePower = ease;
	}

	public increaseProgressByPixels(pixels: number): void {
		this._onInertiaDragStep({ x: pixels, y: 0 });
	}

	//Force the index from external event
	public setIndex(index: number): void {
		this._snap(index, false);
	}

	//Set the require move thershold as a ratio of total amount
	public setStartThreshold(_thershold: number): void {
	}

	//Create the breakpoints:
	private _createBreakpoints(count: number, itemSpacing = null, totalItems = null, snapItems = 1): void {
		if (this.snapWidth) {
			this._createWidthBreakpoints(itemSpacing, totalItems, snapItems);
		} else if (this.loop) {
			this._createLoopBreakPoints(count);
		} else {
			this._createNonLoopBreakPoints(count);
		}
	}

	private _createLoopBreakPoints(count: number): void {
		let ratio: number;
		ratio = 0.0;
		// -0. | 0.0 | 0.125 |

		this._breakpoints = [];

		for (let i = 0; i < count; i++) {
			this._breakpoints.push({
				ratio: ratio,
				upper: ratio + this._breakpointShare / 2,
				lower: ratio - this._breakpointShare / 2
			});

			ratio += this._breakpointShare;
		}

		this._breakpoints.push({
			ratio: 1.0,
			upper: 1.0 + this._breakpointShare / 2,
			lower: 1.0 - this._breakpointShare / 2
		});
	}

	public _createWidthBreakpoints(itemSpacing, itemCount, snapItems = 1) {
		this._breakpointShare = (this.snapWidth + itemSpacing) / (this._virtualWidth - this._innerWidth);

		let ratio, lower, upper: number;

		this._breakpoints = [];

		let snapCount = 0;
		const spaceShare = snapItems * this._breakpointShare;

		for (let i = 0; i < itemCount; i++) {
			snapCount++;

			if (i == 0 || i === itemCount - 1 || snapCount >= snapItems) {
				snapCount = 0;

				if (i === 0) {
					ratio = 0;
					lower = 0;
					upper = spaceShare / 2;
				} else if (i === itemCount - 1) {
					ratio = 1.0;
					lower = 1.0 - spaceShare / 2;
					upper = 1.0;
				} else {
					ratio = this._breakpointShare * i;
					lower = ratio - spaceShare / 2;
					upper = ratio + spaceShare / 2;

					if (lower > 1) lower = 1;
					if (upper > 1) upper = 1;
					if (ratio > 1) ratio = 1;
				}

				this._breakpoints.push({
					ratio: ratio,
					upper: upper,
					lower: lower
				});
			}
		}
	}

	private _createNonLoopBreakPoints(count: number): void {
		let ratio, lower, upper: number;

		this._breakpoints = [];

		const innerShare = 1 / (count - 1);

		for (let i = 0; i < count; i++) {
			if (i === 0) {
				ratio = 0;
				lower = 0;
				upper = this._breakpointShare;
			} else if (i === count - 1) {
				ratio = 1.0;
				lower = 1.0 - this._breakpointShare;
				upper = 1.0;
			} else {
				const r = innerShare * i;
				ratio = i * this._breakpointShare + this._breakpointShare / 2;
				lower = ratio - this._breakpointShare / 2;
				upper = ratio + this._breakpointShare / 2;
				ratio = r;
			}

			this._breakpoints.push({
				ratio: ratio,
				upper: upper,
				lower: lower
			});
		}
	}

	private _addInertiaDrag($target: Element = null): void {
		this._inertiaDrag = new InertiaDrag($target);
		this._inertiaDrag.axis = this.axis === axisType.X ? 'x' : 'y';
		this._inertiaDrag.simulate = true;
		this._inertiaDrag.simulateSteps = this.simulateSteps;
		this._inertiaDrag.stepAfterRelease = false;
		this._inertiaDrag.disableOnRelease = false;
		this._inertiaDrag.onPress = this._onInertiaDragPress.bind(this);
		this._inertiaDrag.onRelease = this._onInertiaDragRelease.bind(this);
	}

	private _onInertiaDragPress(): void {
		if (!this._active || this._cancelled) {
			return;
		}

		this._startIndex = this._breakpointIndex;

		if (this.onPreDrag) {
			this.onPreDrag();
		}
		this._testInOverflowZone();
		this.onStartDrag();
	}

	public lastMoveX;
	private _onInertiaDragStep(stepProgress: Point): void {
		if (!this._active || this._cancelled) {
			return;
		}

		//Reset Ease power
		this._targetEasePower = this._inertiaDrag.eventType == 'touch' ? 1 : this.easePower;
		this._targetDelta = 0.1;

		let increaseRatio: number = -1 * this._getMoveRatio(stepProgress);

		if (Math.abs(increaseRatio) > 0.3) {
			if (increaseRatio < 0) increaseRatio = -0.3;
			else increaseRatio = 0.3;
		}

		this.lastMoveX = increaseRatio;

		//If a drag thershold is required to start. This required when there is also paging scrolling as it's too senstive with out some sort of tolerence

		this._increaseProgress(increaseRatio);
	}

	private _increaseProgress(increaseRatio: number): void {
		this._targetProgress = constrain(
			this._targetProgress + increaseRatio,
			this._constraints.min,
			this._constraints.max
		);
	}

	//Tick function, manages setting progress:

	private _tick(delta: number): void {
		if (!this._active) {
			return;
		}

		delta = window.deltaTime;

		const step = this._inertiaDrag.tick(delta);

		if (step) {
			this._onInertiaDragStep(step);
		}

		//When not using Tweenmax to tween to snap then use stand inernia

		if (!this._tweening) {
			this._easePower = this._easePower + (this._targetEasePower - this._easePower) * this._targetDelta * delta;

			this._progress = this._progress + ((this._targetProgress - this._progress) / this._easePower) * delta;
		}

		//Snap as soon as thersold is crossed
		if (!this._snapping) {
			const snapbreakPointIndex: number = this._getBreakpoint(this._progress);

			if (snapbreakPointIndex !== null && snapbreakPointIndex !== this._breakpointIndex) {
				this._breakpointIndex = snapbreakPointIndex;
				console.log('SET this._breakpointIndex ', this._breakpointIndex);

				if (this.forceSnap) {
					this._inertiaDrag.forceStop();
					this._snap(this._breakpointIndex);
				}
			}
		}

		this.onTick(this._progress);

		if (this._overflowZoneActive) {
			this._testInOverflowZone();
		}
	}

	//Bind to single request animation frame:
	private _bindToAppTick(): void {
		if (App.instance) {
			App.instance.addPreTick((delta: number) => {
				this._tick(delta);
			}, 'dragBreakpoints' + this._randomId);
		}
	}



	//User has release mouse, check what needs to be snapped to:
	private _onInertiaDragRelease(simuatedProgress: Point): void {
		this._targetEasePower =
			this._inertiaDrag.eventType == 'touch' && this.releaseEasePower != 1
				? this.releaseEasePower * 5
				: this.releaseEasePower;
		this._targetDelta = 1;

		this._overflowZoneActive = false;

		this._setConstraints();
		this._constrain();

		if (this.onStopDrag) {
			this.onStopDrag();
		}

		if (!this.snap || (this.snapOnlyOnTouch && this._inertiaDrag.eventType !== 'touch')) {
			DelayHelper.setTimeout(() => this._inertiaDrag.setActive(true), 300);
			DelayHelper.setTimeout(() => (this._snapping = false), 600);
			return;
		}

		//Don't let the swipe move the item over multple widths:
		const allowedSwipeMoveX = device.innerWidth * this._simluateRestrictWidth;

		if (Math.abs(simuatedProgress.x) > allowedSwipeMoveX) {
			simuatedProgress.x = simuatedProgress.x < 0 ? -1 * allowedSwipeMoveX : allowedSwipeMoveX;
		}

		const increaseRatio = this._getMoveRatio(simuatedProgress, true);

		let finalProgress: number = constrain(this._targetProgress - increaseRatio, 0, 1);
		if (finalProgress > 1.07) {
			finalProgress = 1.07;
		}

		let snapbreakPointIndex: number = this._getBreakpoint(finalProgress);

		console.log(
			'STOP DRAG simuatedProgress=',
			simuatedProgress,
			'allowedSwipeMoveX',
			allowedSwipeMoveX,
			'snapbreakPointIndex',
			snapbreakPointIndex,
			'this._breakpointIndex ',
			this._breakpointIndex,
			'this._startIndex',
			this._startIndex
		);

		if (snapbreakPointIndex === null || this._breakpointIndex == null) {
			return;
		}

		if (snapbreakPointIndex != this._startIndex && snapbreakPointIndex - this._startIndex > 1)
			snapbreakPointIndex = this._startIndex + 1;
		else if (snapbreakPointIndex != this._startIndex && snapbreakPointIndex - this._startIndex < 1)
			snapbreakPointIndex = this._startIndex - 1;

		if (snapbreakPointIndex > this._breakpointIndex) {
			snapbreakPointIndex = this._breakpointIndex + 1;
		} else if (snapbreakPointIndex < 0) {
			snapbreakPointIndex = 0;
		}

		console.log('SET this._breakpointIndex  =', (this._breakpointIndex = snapbreakPointIndex));
		this._breakpointIndex = snapbreakPointIndex;

		this._snap(snapbreakPointIndex);
	}

	/** Get swipe move ratio. By comparing to width of container. */
	private _getMoveRatio(point: Point, _simulated: boolean = false): number {
		if (this._inertiaDrag.eventType == 'touch' || this._dragToMoveRatio === 1) {
			const increase = this.axis === axisType.X ? point.x : point.y;
			return increase / (this._virtualWidth - this._innerWidth);
		}

		const dragToMoveRatio = this._inertiaDrag.eventType == 'touch' ? 1 : this._dragToMoveRatio;

		const increase = this.axis === axisType.X ? point.x : point.y;

		if (this._dragMotionType === DragMotionType.RATIO) {
			let sizeRatio = 1;

			//Reduce down impact for smaller than 1920
			if (this._innerWidth < 1920) {
				sizeRatio -= 0.3 * (this._innerWidth / 1920);
			}

			return (increase / ((this._virtualWidth - this._innerWidth) * dragToMoveRatio)) * sizeRatio;
		}
	}

	/** Snap to point: */
	private _snap(snapIndex: number, broadcastEvent: boolean = true): void {
		if (!this._active) {
			return;
		}

		if (this.loop) {
			if (this._breakpoints.length > 3) {
				if (snapIndex > this._breakpoints.length - 2) {
					snapIndex = this._breakpoints.length - 2;
				}
			} else {
				if (snapIndex > this._breakpoints.length - 1) {
					snapIndex = this._breakpoints.length - 1;
				}
			}
		} else {
			if (snapIndex > this._breakpoints.length - 1) {
				snapIndex = this._breakpoints.length - 1;
			}
		}

		this._snapping = true;

		let touchRatio = 0;

		if (!this._inertiaDrag.eventType || this._inertiaDrag.eventType == undefined) {
			this._inertiaDrag.eventType = globals.lastCordsFromTouch ? 'touch' : 'mouse';
		}

		if (this._inertiaDrag.eventType == 'touch') {
			const maxEase = 0.7;
			const minEase = 0.4;
			touchRatio = minEase + (maxEase - minEase) * (device.innerWidth / 1800);
			touchRatio = Math.max(0.35, Math.min(maxEase, touchRatio));
		}

		this._targetEasePower =
			this._inertiaDrag.eventType == 'touch' && this.releaseEasePower != 1
				? this.releaseEasePower * touchRatio
				: this.releaseEasePower;
		this._targetDelta = 1;

		this._breakpointIndex = snapIndex;
		this._targetProgress = this._breakpoints[this._breakpointIndex].ratio;

		if (this.onSnap && broadcastEvent) {
			this.onSnap(this._breakpointIndex);
		}

		DelayHelper.setTimeout(() => this._inertiaDrag.setActive(true), 300);
		DelayHelper.setTimeout(() => (this._snapping = false), 600);
	}

	//Get the breakpoint for a ratio:
	private _getBreakpoint(ratio: number): number {
		for (let i = 0; i < this._breakpoints.length; i++) {
			if (ratio >= this._breakpoints[i].lower && ratio <= this._breakpoints[i].upper) {
				return i;
			}
		}

		return null;
	}

	/** Test if drag is starting in overflow zone */
	private _testInOverflowZone(): void {
		const lower = this._defaultConstraints.min + this._overflowZoneTolerence;
		const upper = this._defaultConstraints.max - this._overflowZoneTolerence;
		this._overflowZoneActive = this._progress < lower || this._progress > upper;
		this._setConstraints();
	}

	/** Set the constraints for the amount the drag is allowed to progress to */
	private _setConstraints(): void {
		if (this._overflowZoneActive) {
			this._constraints.min = this._defaultConstraints.min - this._overflowAmount;
			this._constraints.max = this._defaultConstraints.max + this._overflowAmount;
			return;
		}

		this._constraints.min = this._defaultConstraints.min;
		this._constraints.max = this._defaultConstraints.max;
	}

	/** Constrain the values to meaningful */
	private _constrain(): void {
		this._targetProgress = constrain(this._targetProgress, this._constraints.min, this._constraints.max);
	}
}
